// Turning a fresh sign-in into an unlocked session.

import {
  createAccount,
  aesEncryptString,
  newBackupCodes,
  newTotpSecret,
  unlockWithStretched,
  type AccountKeys,
  type ProtectedAccount,
} from "../crypto";
import { api } from "../lib/api";
import type { Me, Settings } from "../lib/types";
import type { SessionInfo } from "../state/store";

interface MeResponse {
  user: Me;
  account: ProtectedAccount;
  backup: { enc: string; used: number[]; saved: boolean };
  settings: Settings;
  passwordChangedAt: number;
}

/** After the second factor: fetch the protected keys and unlock them. */
export async function openSession(stretchedOrKeys: CryptoKey | AccountKeys): Promise<SessionInfo> {
  const me = await api<MeResponse>("/me");
  const keys =
    stretchedOrKeys instanceof CryptoKey ? await unlockWithStretched(stretchedOrKeys, me.account) : stretchedOrKeys;
  return {
    me: me.user,
    keys,
    account: me.account,
    settings: me.settings,
    backup: me.backup,
    passwordChangedAt: me.passwordChangedAt,
  };
}

/** Everything a new account needs, generated in the browser before anything is sent. */
export async function prepareAccount(name: string, password: string) {
  const { protected: acct, keys, authKeyB64 } = await createAccount(password);
  const totpSecret = newTotpSecret();
  const backupCodes = newBackupCodes();
  const backupEnc = await aesEncryptString(keys.userKey, JSON.stringify(backupCodes));
  return {
    keys,
    totpSecret,
    backupCodes,
    payload: {
      name,
      authKey: authKeyB64,
      kdf: acct.kdf,
      kdfSalt: acct.kdfSaltB64,
      protectedSymmetricKey: acct.protectedSymmetricKey,
      publicKey: acct.publicKeyB64,
      protectedPrivateKey: acct.protectedPrivateKey,
      totpSecret,
      backupCodes,
      backupEnc,
    },
  };
}

export type PreparedAccount = Awaited<ReturnType<typeof prepareAccount>>;

export const REMEMBER_KEY = "agile-vault.email";
export function rememberedEmail(): string {
  try {
    return localStorage.getItem(REMEMBER_KEY) ?? "";
  } catch {
    return "";
  }
}
export function rememberEmail(email: string | null): void {
  try {
    if (email) localStorage.setItem(REMEMBER_KEY, email);
    else localStorage.removeItem(REMEMBER_KEY);
  } catch {
    // Private mode: just ask again next time.
  }
}

export const MIN_PASSWORD = 12;
export function passwordProblem(pw: string, confirm: string): string {
  if (pw.length < MIN_PASSWORD) return `Use at least ${MIN_PASSWORD} characters. A few random words works well.`;
  if (pw !== confirm) return "The two passwords don't match.";
  return "";
}
